import React from "react";
import { ModuleConfig } from "../../types/ModuleConfig";

// Import leadflow components (lazy-loaded for performance)
const Dashboard = React.lazy(() => import("./Dashboard"));
const LeadList = React.lazy(() => import("./LeadList"));
const LeadDetail = React.lazy(() => import("./LeadDetail"));
const FormBuilder = React.lazy(() => import("./FormBuilder"));
const FormEmbed = React.lazy(() => import("./FormEmbed"));
const StageManager = React.lazy(() => import("./StageManager"));
const ActionTemplates = React.lazy(() => import("./ActionTemplates"));
const NotificationSettings = React.lazy(() => import("./NotificationSettings"));
const DomainSettings = React.lazy(() => import("./DomainSettings"));
const EmailBrandingSettings = React.lazy(() => import("./EmailBrandingSettings"));
const PledgeCampaignList = React.lazy(() => import("./PledgeCampaignList"));
const PledgeCampaignEdit = React.lazy(() => import("./PledgeCampaignEdit"));
const PledgeCampaignDetail = React.lazy(() => import("./PledgeCampaignDetail"));
const PublicPledgePage = React.lazy(() => import("./PublicPledgePage"));
const PublicForm = React.lazy(() => import("./PublicForm"));

/**
 * LeadFlow Module Config
 *
 * SINGLE SOURCE OF TRUTH:
 * Permissions are defined ONCE in the backend (leadflow/permissions.ts)
 * and fetched via the modulePermissions GraphQL query.
 *
 * Routes and nav items use `permissionLevel` (e.g. "view", "create", "edit", "delete")
 * which gets resolved to actual permission strings at runtime from the backend.
 *
 * To change permissions: Edit business-builder-backend/src/modules/leadflow/permissions.ts
 */
const leadflowModuleConfig: ModuleConfig = {
  // MODULE IDENTITY
  id: "leadflow",
  name: "LeadFlow",
  description: "Lead capture forms, pipeline management, and marketing automation",
  version: "1.0.0",
  enabled: true,
  category: "business",
  order: 16,
  icon: "FiTarget",
  color: "#38A169",

  // ROUTES
  routes: [
    {
      path: "/leadflow",
      component: Dashboard,
      permissionLevel: "view",
      useLayout: true,
    },
    {
      path: "/leadflow/leads",
      component: LeadList,
      permissionLevel: "view",
      useLayout: true,
    },
    {
      path: "/leadflow/leads/:id",
      component: LeadDetail,
      permissionLevel: "view",
      useLayout: true,
    },
    {
      path: "/leadflow/forms",
      component: FormBuilder,
      permissionLevel: "edit",
      useLayout: true,
    },
    {
      path: "/leadflow/forms/:id/embed",
      component: FormEmbed,
      permissionLevel: "view",
      useLayout: true,
    },
    {
      path: "/leadflow/stages",
      component: StageManager,
      permissionLevel: "edit",
      useLayout: true,
    },
    {
      path: "/leadflow/actions",
      component: ActionTemplates,
      permissionLevel: "edit",
      useLayout: true,
    },
    {
      path: "/leadflow/notifications",
      component: NotificationSettings,
      permissionLevel: "delete",
      useLayout: true,
    },
    {
      path: "/leadflow/domains",
      component: DomainSettings,
      permissionLevel: "edit",
      useLayout: true,
    },
    {
      path: "/leadflow/email-branding",
      component: EmailBrandingSettings,
      permissionLevel: "edit",
      useLayout: true,
    },
    // Pledge campaigns (admin)
    {
      path: "/leadflow/pledge-campaigns",
      component: PledgeCampaignList,
      permissionLevel: "view",
      useLayout: true,
    },
    {
      path: "/leadflow/pledge-campaigns/new",
      component: PledgeCampaignEdit,
      permissionLevel: "create",
      useLayout: true,
    },
    {
      path: "/leadflow/pledge-campaigns/:id",
      component: PledgeCampaignDetail,
      permissionLevel: "view",
      useLayout: true,
    },
    {
      path: "/leadflow/pledge-campaigns/:id/edit",
      component: PledgeCampaignEdit,
      permissionLevel: "edit",
      useLayout: true,
    },
    // Public pledge page — NO permissions/permissionLevel so ProtectedRoute lets it through.
    // See vault/how-to/seamless-page-auth-pattern.md §"Public routes still work".
    // useLayout: true — brings the tenant's NavbarWithCallToAction + FooterWithFourColumns
    // wrappers via ModulePageLayout. noContainer + noBreadcrumb let the page run
    // edge-to-edge with its own gradient hero.
    {
      path: "/pledge/:slug",
      component: PublicPledgePage,
      permissions: [],
      useLayout: true,
      layoutOptions: { noContainer: true, noBreadcrumb: true },
    } as any,

    // PUBLIC form (lead capture) — same pattern as /pledge/:slug.
    // Backfills the documented-but-never-registered route. Used by the
    // 2026-04-23 Anna summary email for /form/book-gift. PublicForm.tsx
    // already reads `slug` from useParams (falls back to prop when embedded).
    {
      path: "/form/:slug",
      component: PublicForm,
      permissions: [],
      useLayout: true,
      layoutOptions: { noContainer: true, noBreadcrumb: true },
    } as any,
  ],

  // NAVIGATION
  navigation: [
    {
      label: "Dashboard",
      path: "/leadflow",
      icon: "📊",
      permissionLevel: "view",
    },
    {
      label: "Leads",
      path: "/leadflow/leads",
      icon: "👥",
      permissionLevel: "view",
    },
    {
      label: "Forms",
      path: "/leadflow/forms",
      icon: "📝",
      permissionLevel: "edit",
    },
    {
      label: "Stages",
      path: "/leadflow/stages",
      icon: "🔀",
      permissionLevel: "edit",
    },
    {
      label: "Actions",
      path: "/leadflow/actions",
      icon: "📨",
      permissionLevel: "edit",
    },
    {
      label: "Notifications",
      path: "/leadflow/notifications",
      icon: "🔔",
      permissionLevel: "delete",
    },
    {
      label: "Domains",
      path: "/leadflow/domains",
      icon: "🌐",
      permissionLevel: "edit",
    },
    {
      label: "Email Branding",
      path: "/leadflow/email-branding",
      icon: "🎨",
      permissionLevel: "edit",
    },
    {
      label: "Pledge Campaigns",
      path: "/leadflow/pledge-campaigns",
      icon: "🤝",
      permissionLevel: "view",
    },
  ],

  // PERMISSIONS - resolved from backend at runtime
  permissions: {
    view: [],
    create: [],
    edit: [],
    delete: [],
  },

  // QUICK ACTIONS
  quickActions: [
    {
      label: "View Leads",
      path: "/leadflow/leads",
      icon: "FiUsers",
      description: "View and manage captured leads",
    },
    {
      label: "Manage Forms",
      path: "/leadflow/forms",
      icon: "FiFileText",
      description: "Create and configure lead capture forms",
    },
  ],

  // CHANGELOG
  changelog: [
    {
      version: "1.0.0",
      releaseDate: "2026-03-23",
      title: "Initial Release - LeadFlow",
      features: [
        "Lead capture via embeddable forms",
        "Custom pipeline stages",
        "Email/SMS action templates",
        "Domain registration for CORS",
        "Notification rules",
        "Lead-to-client conversion",
      ],
      notes: "Initial release of the LeadFlow module for lead capture and marketing.",
    },
  ],

  // ROADMAP
  roadmap: [
    {
      title: "Kanban Board View",
      description: "Drag-and-drop kanban board for visual lead pipeline management",
      priority: "high" as const,
      status: "planned" as const,
      features: ["Drag leads between stages", "Visual pipeline overview"],
    },
    {
      title: "Automated Drip Campaigns",
      description: "Schedule sequences of emails/SMS to leads based on stage changes",
      priority: "medium" as const,
      status: "planned" as const,
      features: ["Multi-step sequences", "Stage-based triggers"],
    },
    {
      title: "Form Analytics",
      description: "Submission rates, conversion tracking, and A/B testing for forms",
      priority: "medium" as const,
      status: "planned" as const,
      features: ["Submission rates", "Conversion tracking", "A/B testing"],
    },
    {
      title: "Calendar Integration",
      description: "Schedule follow-up calls and meetings directly from lead detail page",
      priority: "low" as const,
      status: "planned" as const,
      features: ["One-click follow-up scheduling", "Calendar sync"],
    },
  ],
};

export default leadflowModuleConfig;
